import { ActionIcon, Menu, Tooltip } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import {
  type MRT_Row,
  type MRT_RowData,
  type MRT_TableInstance,
} from "mantine-react-table";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { useTranslation } from "react-i18next";

interface ExportButtonProps {
  table: MRT_TableInstance<MRT_RowData>;
  tableName?: string;
  viewName?: string;
  fetchAllData?: () => Promise<MRT_RowData[]>;
  onLoadingChange?: (loading: boolean) => void;
}

export default function ExportButton({
  table,
  tableName = "data",
  viewName = "view",
  fetchAllData,
  onLoadingChange,
}: ExportButtonProps) {
  const { t } = useTranslation("features");
  const handleExportRows = async (
    rows: MRT_Row<MRT_RowData>[],
    filename: string,
  ) => {
    // Get visible columns from the table
    const visibleColumns = table.getVisibleLeafColumns().map((col) => col.id);

    const rowData = rows.map((row) => {
      const data: Record<string, string | number | boolean | null | undefined> =
        {};

      // Only process visible columns
      Object.entries(row.original).forEach(([key, value]) => {
        if (!visibleColumns.includes(key)) return; // Skip hidden columns

        if (value === null || value === undefined) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          data[key] = value;
        } else if (
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean"
        ) {
          data[key] = `"${value.toString()}"`;
        } else if (typeof value === "object") {
          if (Object.keys(value as object).includes("id")) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            data[key] = value.id;
          } else {
            data[key] = null;
          }
        } else {
          data[key] = String(value);
        }
      });

      return data;
    });

    const csvConfig = mkConfig({
      fieldSeparator: ",",
      decimalSeparator: ".",
      useKeysAsHeaders: true,
      filename,
    });

    // Use setTimeout to ensure CSV generation happens on next tick
    await new Promise<void>((resolve) => {
      setTimeout(() => {
        const csv = generateCsv(csvConfig)(rowData);
        download(csvConfig)(csv);
        resolve();
      }, 0);
    });
  };

  const handleExportAllData = async () => {
    onLoadingChange?.(true);
    try {
      const filename = `${tableName}_${viewName.replace(" ", "_")}_all${new Date().toISOString().split("T")[0]}`;

      if (fetchAllData) {
        const allData = await fetchAllData();

        const mockRows = allData.map((data) => ({
          original: data,
        })) as MRT_Row<MRT_RowData>[];

        await handleExportRows(mockRows, filename);
      } else {
        console.error("No fetchAllData function provided");
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      onLoadingChange?.(false);
    }
  };

  const handleExportPageData = async () => {
    onLoadingChange?.(true);
    try {
      const filename = `${tableName}_${viewName.replace(" ", "_")}_page${new Date().toISOString().split("T")[0]}`;
      await handleExportRows(table.getRowModel().rows, filename);
    } finally {
      onLoadingChange?.(false);
    }
  };

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <Tooltip label={t("export.tooltip")} position="top">
          <ActionIcon color="#757575" variant="subtle" size="lg">
            <IconDownload size={24} />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item onClick={handleExportAllData}>
          {t("export.exportAllData")}
        </Menu.Item>
        <Menu.Item onClick={handleExportPageData}>
          {t("export.exportPageData")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
